<template>
  <div class="page-container">
    <!-- 顶部搜索栏 -->
    <div class="top-container">
      <el-form class="power-form" inline :model="loadForm">
        <div class="form-left-items">
          <el-form-item :label="$str.getKey('数据分析/日期')">
            <el-date-picker v-model="loadForm.timer" @change="timeRangeChange" :disabled-date="disabledDate"
              :range-separator="$str.getKey('数据分析/至')" :clearable="false" type="daterange" format="YYYY-MM-DD"
              size="default" class="filter-date-picker"></el-date-picker>
          </el-form-item>
        </div>
      </el-form>
      <el-form-item>
        <div class="filter-actions">
          <v-btn color="#2655f1" rounded="xl" class="action-btn" @click="onSearch">
            <v-icon start icon="mdi-magnify" />{{ $str.getKey('数据分析/查询') }}
          </v-btn>
          <v-btn variant="outlined" color="#2655f1" rounded="xl" class="action-btn" @click="onExport">
            <v-icon start icon="mdi-download" />{{ $str.getKey('数据分析/导出') }}
          </v-btn>
          <v-btn variant="outlined" color="#2655f1" rounded="xl" class="action-btn" @click="onReset">
            <v-icon start icon="mdi-refresh" />{{ $str.getKey('数据分析/重置') }}
          </v-btn>
        </div>
      </el-form-item>
    </div>
    <!-- 图表区域 -->
    <div class="main-container">
      <div class="left-container">
        <div class="cards-container">
          <el-scrollbar height="100%">
            <template v-for="(itemData) in selectOptions" :key="itemData">
              <SelectCard :cardInfo="itemData" :config="cardConfig" @updateVirtualMeter="refreshCharts"
                @updateDatumLine="refreshCharts" @selectLinkChanged="selectLinkChanged" @updateMmdLine="refreshMmdLine">
              </SelectCard>
            </template>
          </el-scrollbar>
        </div>
        <div class="bottom-container">
          <el-button type="text" @click="isShowTopo = true">{{ $str.getKey('数据分析/查看拓扑') }}</el-button>
        </div>
      </div>
      <div class="right-container">
        <div class="chart-title">{{ $str.getKey('数据分析/功率曲线') }}</div>
        <div ref="linesChartDom" class="chart-container"></div>
        <!-- 加载状态 -->
        <div v-if="chartLoading" class="chart-status">
          <div class="status-content">
            <span class="loading-text">{{ $str.getKey('数据分析/加载中...') }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- 弹窗-查看拓扑 -->
  <viewTopoModal v-if="isShowTopo" v-model:show="isShowTopo" @SendChange="isShowTopo = false;" :stationId="stationId" />
</template>

<script setup>
import { ref, reactive, computed, onMounted, onBeforeUnmount } from "vue";
import * as echarts from 'echarts';
import { ElMessageBox } from "element-plus";
import dayjs from "dayjs";
import { cloneDeep } from "@/utils"
import SelectCard from './selectCard.vue'
import viewTopoModal from "./viewTopoModal.vue"; // 弹窗-查看拓扑
import { EntityLoadTracking } from "./entityLoadTracking.js";
import { useAppStore } from "@/store/modules/app";

const $str = window.$str;
const appStore = useAppStore();
const { stationId } = appStore.useStationState({
  stationIdWatch() {
    stationIdChangeCb(stationId.value);
  }
});
const stationName = computed(() => { return appStore.stationName; })

const originLoadForm = {
  timer: [dayjs(), dayjs()],
}
const loadForm = reactive(cloneDeep(originLoadForm));

const updateCardData = (groupKey) => {
  // return selectOptions[key].data;
  const cardInfo = selectOptions.find(it => it.groupKey === groupKey);
  return cardInfo.data;
}
const cardConfig = reactive({
  refreshTime: dayjs().format("YYYY-MM-DD HH:mm:ss"),
  getNewData: updateCardData,
})



/** 初始化一个辅助类，处理数据获取&图表渲染相关的的各种业务逻辑 ====== */
const loadTrackingOptions = {
  startTime: dayjs(loadForm.timer[0]).format("YYYY-MM-DD ") + "00:00:00",
  endTime: dayjs(loadForm.timer[1]).format("YYYY-MM-DD ") + "23:59:59",
  mmdMonth: dayjs().subtract(1, 'month'),
}
const loadTracking = new EntityLoadTracking(stationId.value, loadTrackingOptions);

const selectOptions = [
  {
    groupKey: 'entityMeters',
    name: $str.getKey('数据分析/实体电表'),
    data: [],
  },
  {
    groupKey: 'virtualMeters',
    name: $str.getKey('数据分析/虚拟电表'),
    data: [],
  },
  {
    groupKey: 'datumLines',
    name: $str.getKey('数据分析/基准线'),
    data: [],
  },
  {
    groupKey: 'allDemands',
    name: $str.getKey('数据分析/需量保护阈值'),
    data: [],
  },
];
function refreshSelectOptions() {
  selectOptions.forEach(card => {
    card.data = loadTracking.selectOptions[card.groupKey]?.data ?? [];
  })
  cardConfig.refreshTime = dayjs().format("YYYY-MM-DD HH:mm:ss:SSS"); // 更新刷新时间，触发card更新选项数据
}



const isShowTopo = ref(false);

const linesChartDom = ref(null);
let linesChartObj = null;
const chartLoading = ref(false); // 图表加载状态


onMounted(() => {
  linesChartObj = echarts.init(linesChartDom.value, null, {
    renderer: 'canvas',
    devicePixelRatio: window.devicePixelRatio || 1,
  });
  observeChartResize();
  refreshCharts()
})


function initPageByStation(id) {
  const opfData = cloneDeep(originLoadForm);
  loadForm.timer = opfData.timer;
  loadTracking.updateTimeRange(...loadForm.timer);
  chartLoading.value = true;
  loadTracking.updateStationId(id).then(() => {
    const timerId = setInterval(() => {
      if (linesChartObj && timerId) {
        clearInterval(timerId);
        loadTracking.initCharts(linesChartObj).then(() => {
          refreshSelectOptions();
          chartLoading.value = false;
        }).catch(() => {
          chartLoading.value = false;
        });
      }
    }, 100);
  });
}


function stationIdChangeCb(id) {
  initPageByStation(id);
}


function timeRangeChange() {
  if (loadForm.timer && loadForm.timer.length === 2) {
    const start = dayjs(loadForm.timer[0]);
    const end = dayjs(loadForm.timer[1]);
    loadForm.timer = [start, end];
    if (checkDateRange()) {
      onSearch();
    }
  }
}

function checkDateRange(range = 1) {
  const st = dayjs(loadForm.timer[0]);
  const et = dayjs(loadForm.timer[1]);
  const limitDay = st.add(range, 'month');
  if (et >= limitDay) {
    window.dispatchNotify({
      type: 'info',
      message: $str.getKey('数据分析/日期范围过大，请选择范围为1个月内的日期')
    })
    return false;
  }
  return true;
}

function disabledDate(date) {
  const latestHalfYear = dayjs().subtract(6, 'month');
  return dayjs(date) > dayjs() || dayjs(date) < latestHalfYear;
}

function onSearch() {
  if (!loadForm.timer || loadForm.timer.length !== 2) {
    window.dispatchNotify({
      type: 'warning',
      message: $str.getKey('数据分析/请选择日期范围')
    });
    return false;
  }

  loadTracking.updateTimeRange(...loadForm.timer);
  chartLoading.value = true;

  loadTracking.updateCharts(linesChartObj).then(() => {
    refreshSelectOptions();
    chartLoading.value = false;
  }).catch(() => {
    chartLoading.value = false;
  });
}

function selectLinkChanged(msg) {
  loadTracking.updateChartsLegend(linesChartObj, msg.groupKey, msg.checkedLinks);
}

function refreshMmdLine(month) {
  loadTracking.refreshMonthlyMaxDemandLine(linesChartObj, month);
}

function onExport() {
  if (!loadForm.timer || loadForm.timer.length !== 2) {
    window.dispatchNotify({
      type: 'warning',
      message: $str.getKey('数据分析/请先选择日期范围')
    });
    return;
  }

  ElMessageBox.confirm(
    $str.getKey('数据分析/是否确认导出负荷跟踪数据？'),
    $str.getKey('数据分析/提示'),
    {
      confirmButtonText: $str.getKey('数据分析/确认'),
      cancelButtonText: $str.getKey('数据分析/取消'),
      confirmButtonClass: 'custom-confirm-btn',
      cancelButtonClass: 'custom-cancel-btn',
      type: "warning"
    }
  ).then(() => {
    const uuidList = loadTracking.getLegendSelectedList();
    const params = {
      startTime: dayjs(loadForm.timer[0]).format("YYYY-MM-DD ") + "00:00:00",
      endTime: dayjs(loadForm.timer[1]).format("YYYY-MM-DD ") + "23:59:59",
      stationId: stationId.value,
      meterUuidList: uuidList.join(","),
    }
    loadTracking.exportLoadTrackingData(params).then(() => {
      window.dispatchNotify({
        type: 'success',
        message: $str.getKey('数据分析/导出成功')
      })
    });
  }).catch(() => { });
}

function onReset() {
  const opfData = cloneDeep(originLoadForm);
  loadForm.timer = opfData.timer;

  onSearch();
}

function refreshCharts(id = stationId.value) {
  chartLoading.value = true;
  loadTracking.updateCharts(linesChartObj).then(() => {
    refreshSelectOptions();
    chartLoading.value = false;
  }).catch(() => {
    chartLoading.value = false;
  });
}

// 容器由 flex 布局撑开，侧边栏折叠等场景不会触发 window 的 resize，
// 只靠 window resize 会让 canvas 被 CSS 拉伸从而文字发虚，这里改用 ResizeObserver 观察容器本身
let resizeObserver = null;
function observeChartResize() {
  if (!linesChartDom.value || typeof ResizeObserver === 'undefined') return;
  resizeObserver = new ResizeObserver(() => {
    linesChartObj?.resize();
  });
  resizeObserver.observe(linesChartDom.value);
}

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  resizeObserver = null;
  linesChartObj?.dispose();
  linesChartObj = null;
})
</script>

<style lang="scss" scoped>
.page-container {
  font-family: PingFang-SC, PingFang-SC;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;

  .top-container {
    height: auto;
    padding: 0 24px 14px 24px;
    box-sizing: border-box;
    background: rgba(255, 255, 255, 1);
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;

    .form-left-items {
      display: flex;
      align-items: center;
      gap: 16px;
      flex-wrap: wrap;
    }

    .power-form {
      flex: 1 1 auto;
      min-width: 0;
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 16px;
    }

    .el-form-item {
      margin-bottom: 0;
      margin-right: 0;
      display: flex;
      align-items: center;
      height: 40px;

      :deep(.el-form-item__label) {
        font-size: 14px;
        color: rgba(46, 46, 46, 0.85);
        font-weight: 400;
        padding-right: 12px;
        line-height: 40px;
        height: 40px;
        display: flex;
        align-items: center;
      }

      :deep(.el-form-item__content) {
        line-height: 40px;
        display: flex;
        align-items: center;

        .el-range-editor {
          width: 260px !important;
          min-width: 260px !important;
          max-width: 260px !important;
        }
      }
    }

    .filter-date-picker {

      :deep(.el-input__wrapper) {
        width: 100% !important;
        max-width: 100% !important;
        background: #fff;
        border: 1px solid rgba(192, 207, 218, 0.3);
        border-radius: 20px;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        padding: 0 16px;
        min-height: 40px;
        height: 40px;
        transition: border-color 0.2s;

        &.is-focus {
          border-color: #3a68ff;
        }
      }

      :deep(.el-input__inner) {
        font-family: "HarmonyOS Sans SC", sans-serif;
        font-size: 14px;
        color: #2E2E2E;
        height: 40px;
        line-height: 40px;

        &::placeholder {
          color: rgba(46, 46, 46, 0.5);
          font-weight: 400;
          font-family: "HarmonyOS Sans SC", sans-serif;
        }
      }

      :deep(.el-input__suffix) {
        color: rgba(46, 46, 46, 0.5);
      }

      :deep(.el-range-input) {
        font-family: "HarmonyOS Sans SC", sans-serif;
        font-size: 14px;
        color: #2E2E2E;
      }

      :deep(.el-range-separator) {
        font-family: "HarmonyOS Sans SC", sans-serif;
        font-size: 14px;
        color: #2E2E2E;
      }
    }

    .filter-actions {
      display: flex;
      align-items: center;
      gap: 12px;
      flex-shrink: 0;
      height: 40px;
    }
  }

  .main-container {
    flex: 1;
    display: flex;
    flex-direction: row;
    gap: 16px;
    min-height: 0;

    .left-container {
      width: 20%;
      height: 100%;
      box-sizing: border-box;
      background: rgba(255, 255, 255, 1);
      border-radius: 20px;
      padding: 20px;
      display: flex;
      flex-direction: column;

      .cards-container {
        height: calc(100% - 40px);
        flex: 1;

        ::v-deep .el-scrollbar__bar.is-vertical,
        .el-virtual-scrollbar.el-vl__vertical {
          display: none;
        }
      }

      .bottom-container {
        height: 36px;
        display: flex;
        justify-content: center;
        align-items: center;

        // 设置文字类型的btn，hover和focus时，不展示默认背景色
        :deep(.el-button--text:not(.is-disabled)) {
          color: rgba(46, 46, 46, 0.85);
          font-size: 14px;
          padding: 0;

          &:hover,
          &:focus {
            background-color: transparent;
            color: #3a68ff;
          }
        }
      }
    }

    .right-container {
      flex: 1;
      width: 80%;
      box-sizing: border-box;
      background: rgba(255, 255, 255, 1);
      border-radius: 20px;
      padding: 20px;
      display: flex;
      flex-direction: column;
      min-height: 0;
      position: relative;

      .chart-title {
        font-family: 'HarmonyOS Sans SC', sans-serif;
        font-size: 16px;
        color: rgba(46, 46, 46, 1);
        font-weight: 500;
        margin-bottom: 20px;
      }

      .chart-container {
        flex: 1;
        width: 100%;
        min-height: 0;
      }

      .chart-status {
        position: absolute;
        top: 60px;
        left: 20px;
        right: 20px;
        bottom: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(255, 255, 255, 0.9);
        z-index: 10;

        .status-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 12px;

          .loading-text {
            font-family: "HarmonyOS Sans SC", sans-serif;
            font-size: 14px;
            color: rgba(46, 46, 46, 0.65);
            font-weight: 400;
          }
        }
      }
    }
  }

  // 设置图表tooltip的样式
  ::v-deep .chart-tooltip {
    padding: 12px 16px !important;
    border-radius: 8px !important;
    // 半透明背景 + backdrop-filter 会让 tooltip 里的文字发虚，改为不透明背景 + 阴影
    background: #fff !important;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12) !important;
    border: none !important;
  }

  ::v-deep .chart-tooltip__time {
    margin-bottom: 8px;
    font-family: "PingFang SC";
    font-size: 12px;
    font-weight: 400;
    line-height: 20px;
    color: #6A727C;
  }

  ::v-deep .chart-tooltip__items-container {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  ::v-deep .chart-tooltip__series {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding: 4px 8px;
    border-radius: 6px;
  }

  ::v-deep .chart-tooltip__series-name,
  ::v-deep .chart-tooltip__value {
    font-family: "PingFang SC";
    font-size: 12px;
    font-weight: 400;
    line-height: 20px;
    margin-left: 4px;
    color: var(--text-icon-font-gy-190-primary, rgba(0, 0, 0, 0.90));
  }

  ::v-deep .chart-tooltip__point {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    margin-right: 4px;
    flex-shrink: 0;
  }
}

.el-date-editor {
  .el-range__close-icon {
    display: none !important;
  }
}

// 选中日期时的颜色
.el-date-editor.el-input__wrapper:hover {
  box-shadow: none !important;
}
</style>
